<template>
  <div class="step-mapping">
    <v-alert type="warning" variant="tonal" border="start" rounded="xl" class="mt-2">
      Найдено <strong>{{ context.conflictCount }}</strong> конфликтов.
      Заполните решения для всех пунктов и примените их одним действием.
    </v-alert>

    <v-alert
      v-if="context.missingDepartmentsConfig"
      type="error"
      variant="tonal"
      border="start"
      rounded="xl"
    >
      Для импорта группы "как есть" нужно указать филиал, но в справочнике филиалов пока нет данных.
      <a :href="$model.wwwroot + '/blocks/manage_groups/plan/directory.php?dir=departments'">
        Заполните информацию хотя бы по одному филиалу
      </a>.
    </v-alert>

    <v-sheet class="step-mapping__summary" rounded="xl">
      <div class="step-mapping__summary-head">
        <div>
          <div class="step-mapping__summary-caption">
            Заполнено {{ resolvedCount }} из {{ context.conflictCount }} конфликтов
          </div>
        </div>
        <div class="step-mapping__summary-meta">
          <v-chip color="error" variant="tonal" size="small">
            Без решения: {{ unresolvedConflicts.length }}
          </v-chip>
          <v-chip color="success" variant="tonal" size="small">
            Готово: {{ resolvedConflicts.length }}
          </v-chip>
        </div>
      </div>

      <v-progress-linear
        class="mt-4"
        :model-value="progressValue"
        color="primary"
        height="10"
        rounded
      />

      <div class="step-mapping__summary-actions">
        <v-btn
          variant="text"
          color="default"
          :disabled="resolvedCount === 0 || loading"
          @click="resetResolutions"
        >
          Сбросить выбор
        </v-btn>
        <v-btn
          color="primary"
          size="large"
          :loading="loading"
          :disabled="!allResolved"
          @click="submitResolutions"
        >
          Далее
        </v-btn>
      </div>
    </v-sheet>

    <div v-if="unresolvedConflicts.length" class="step-mapping__groups">
      <div
        v-for="section in unresolvedConflictSections"
        :key="section.key"
        class="step-mapping__group-section"
      >
        <div v-if="section.title" class="step-mapping__group-title">
          {{ section.title }}
        </div>
        <v-row>
          <v-col v-for="conflict in section.conflicts" :key="buildConflictKey(conflict)" cols="12">
            <ConflictResolutionCard
              :conflict="conflict"
              :model-value="localResolutions[buildConflictKey(conflict)] || null"
              @update:model-value="updateResolution(buildConflictKey(conflict), $event)"
            />
          </v-col>
        </v-row>
      </div>
    </div>

    <v-expansion-panels
      v-if="resolvedConflicts.length"
      class="step-mapping__resolved"
      variant="accordion"
    >
      <v-expansion-panel rounded="xl" elevation="0">
        <v-expansion-panel-title>
          Уже заполненные решения ({{ resolvedConflicts.length }})
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <div
            v-for="section in resolvedConflictSections"
            :key="section.key"
            class="step-mapping__group-section"
          >
            <div v-if="section.title" class="step-mapping__group-title">
              {{ section.title }}
            </div>
            <v-row>
              <v-col v-for="conflict in section.conflicts" :key="buildConflictKey(conflict)" cols="12">
                <ConflictResolutionCard
                  :conflict="conflict"
                  :model-value="localResolutions[buildConflictKey(conflict)] || null"
                  @update:model-value="updateResolution(buildConflictKey(conflict), $event)"
                />
              </v-col>
            </v-row>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-sheet
      v-if="!unresolvedConflicts.length && context.conflictCount > 0"
      class="step-mapping__ready"
      rounded="xl"
    >
      <div class="step-mapping__ready-title">Все конфликты заполнены</div>
      <div class="step-mapping__ready-caption">
        Проверьте выбранные варианты и примените их одним запросом.
      </div>
    </v-sheet>
  </div>
</template>

<script>
import ConflictResolutionCard from "./ConflictResolutionCard.vue";

export default {
  name: "StepMapping",
  components: {
    ConflictResolutionCard,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      localResolutions: {},
    };
  },
  computed: {
    resolvedCount() {
      return this.context.conflicts.reduce(
        (count, conflict) => count + (this.hasResolutionForConflict(conflict) ? 1 : 0),
        0
      );
    },
    unresolvedConflicts() {
      return this.context.conflicts.filter((conflict) => !this.hasResolutionForConflict(conflict));
    },
    unresolvedConflictSections() {
      return this.groupConflictsByDepartment(this.unresolvedConflicts);
    },
    resolvedConflicts() {
      return this.context.conflicts.filter((conflict) => this.hasResolutionForConflict(conflict));
    },
    resolvedConflictSections() {
      return this.groupConflictsByDepartment(this.resolvedConflicts);
    },
    allResolved() {
      return this.context.conflicts.length > 0 && this.unresolvedConflicts.length === 0;
    },
    progressValue() {
      if (!this.context.conflictCount) {
        return 0;
      }

      return Math.round((this.resolvedCount / this.context.conflictCount) * 100);
    },
  },
  watch: {
    "context.conflicts": {
      immediate: true,
      deep: true,
      handler(conflicts) {
        const next = {};
        (conflicts || []).forEach((conflict) => {
          const key = this.buildConflictKey(conflict);
          next[key] = this.localResolutions[key] ?? null;
        });
        this.localResolutions = next;
      },
    },
  },
  methods: {
    buildConflictKey(conflict) {
      return `${conflict.code}:${JSON.stringify(conflict.targetId)}`;
    },
    hasResolutionForConflict(conflict) {
      const value = this.localResolutions[this.buildConflictKey(conflict)];
      return Boolean(value && value.action);
    },
    updateResolution(key, value) {
      this.localResolutions = {
        ...this.localResolutions,
        [key]: value,
      };
    },
    resetResolutions() {
      if (this.loading) {
        return;
      }

      const cleared = {};
      this.context.conflicts.forEach((conflict) => {
        cleared[this.buildConflictKey(conflict)] = null;
      });
      this.localResolutions = cleared;
    },
    submitResolutions() {
      if (!this.allResolved || this.loading) {
        return;
      }

      const resolutions = this.context.conflicts
        .map((conflict) => this.localResolutions[this.buildConflictKey(conflict)])
        .filter(Boolean);

      const formData = new FormData();
      formData.append("sessionId", String(this.context.sessionId));
      formData.append("resolutions", JSON.stringify(resolutions));

      this.loading = true;
      this.axios
        .post(
          this.$model.wwwroot + "/procollege/manage_groups/timetable2/importCity/applyCityTTConflicts",
          formData
        )
        .then((response) => {
          this.$emit("resolved", response.data);
        })
        .catch((error) => {
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    groupConflictsByDepartment(conflicts) {
      const sections = [];
      let currentSection = null;

      const sortedConflicts = [...conflicts].sort((left, right) => {
        const leftTitle = this.getConflictDepartmentName(left);
        const rightTitle = this.getConflictDepartmentName(right);

        if (leftTitle !== rightTitle) {
          if (!leftTitle) {
            return 1;
          }

          if (!rightTitle) {
            return -1;
          }

          return leftTitle.localeCompare(rightTitle, "ru");
        }

        return left.message.localeCompare(right.message, "ru");
      });

      sortedConflicts.forEach((conflict) => {
        const title = this.getConflictDepartmentName(conflict);
        const key = title || "__ungrouped__";

        if (!currentSection || currentSection.key !== key) {
          currentSection = {
            key,
            title,
            conflicts: [],
          };
          sections.push(currentSection);
        }

        currentSection.conflicts.push(conflict);
      });

      return sections;
    },
    getConflictDepartmentName(conflict) {
      return conflict?.details?.departmentName || "";
    },
  },
};
</script>

<style scoped>
.step-mapping {
  display: grid;
  gap: 20px;
}

.step-mapping__summary {
  padding: 20px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background:
    radial-gradient(circle at top left, rgba(14, 165, 233, 0.08), transparent 24%),
    linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.step-mapping__summary-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.step-mapping__summary-title {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}

.step-mapping__summary-caption {
  margin-top: 6px;
  color: #64748b;
}

.step-mapping__summary-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.step-mapping__summary-actions {
  margin-top: 18px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.step-mapping__groups {
  display: grid;
  gap: 20px;
}

.step-mapping__group-section {
  display: grid;
  gap: 12px;
}

.step-mapping__group-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.step-mapping__resolved :deep(.v-expansion-panel) {
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.82);
}

.step-mapping__ready {
  padding: 22px;
  border: 1px solid rgba(34, 197, 94, 0.18);
  background:
    radial-gradient(circle at top left, rgba(74, 222, 128, 0.12), transparent 28%),
    linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.step-mapping__ready-title {
  font-size: 18px;
  font-weight: 700;
  color: #166534;
}

.step-mapping__ready-caption {
  margin-top: 8px;
  color: #475569;
}

@media (max-width: 768px) {
  .step-mapping__summary-head {
    flex-direction: column;
  }

  .step-mapping__summary-meta {
    justify-content: flex-start;
  }

  .step-mapping__summary-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
}
</style>
